import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {memo, useId} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {ModalDialog, ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {usePrefersReducedMotion} from "@repo/data-prefers-reduced-motion";

// TODO: Rename openModal/closeModal to something sheet related – maybe just
// open/close?
type Props = ControlProps & {
    content:
        | React.ReactElement
        | ((props: {closeModal: () => void}) => React.ReactElement);
    style?: StyleType;
};

// Props used to determine if the sheet is open. Either some children that can
// open the sheet, or a boolean and close button for a controlled sheet.
type ControlProps =
    | {
          children: (props: {openModal: () => unknown}) => React.ReactNode;
      }
    | {
          opened: boolean;
          onClose: () => void;
      };

const BottomSheet = memo(
    ({content, style, ...restProps}: Props): React.ReactElement => {
        const prefersReducedMotion = usePrefersReducedMotion();
        // The ID used by Screen Readers to identify the dialog title.
        const dialogTitleId = useId();

        return (
            <ModalLauncher
                {...restProps}
                modal={({closeModal}) => (
                    <ModalDialog
                        style={[
                            styles.modal,
                            !prefersReducedMotion && styles.openingAnimation,
                            style,
                        ]}
                        aria-labelledby={dialogTitleId}
                    >
                        <View style={styles.contentContainer}>
                            {typeof content === "function"
                                ? content({closeModal})
                                : content}
                        </View>
                        <View style={styles.closeButtonContainer}>
                            <Button kind="tertiary" onClick={closeModal}>
                                {t`Close`}
                            </Button>
                        </View>
                    </ModalDialog>
                )}
            />
        );
    },
);

export default BottomSheet;

const slideOpen = {
    "0%": {
        transform: "translateY(100%)",
    },

    "100%": {
        transform: "translateY(0%)",
    },
} as const;

// TODO (LP-13098): Animate BottomSheet component as designed
const styles = StyleSheet.create({
    closeButtonContainer: {
        alignItems: "center",
        padding: spacing.medium_16,
        paddingTop: 0,
    },
    contentContainer: {
        flex: 1,
        padding: spacing.medium_16,
    },
    modal: {
        backgroundColor: color.white,
        borderRadius: `${spacing.small_12}px ${spacing.small_12}px 0px 0px`,
        bottom: 0,
        height: "auto",
        width: "100vw",
        padding: 0,
        position: "fixed",
    },
    openingAnimation: {
        // @ts-expect-error - TS2322 - Type '{ readonly "0%": { readonly transform: "translateY(100%)"; }; readonly "100%": { readonly transform: "translateY(0%)"; }; }' is not assignable to type 'string'.
        animationName: [slideOpen],
        animationDuration: "300ms",
        animationIterationCount: 1,
    },
});
